import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FileSearch, Loader2, Eye, EyeOff, Shield } from 'lucide-react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await login(username, password);
      toast.success('Welcome back!', {
        description: 'Successfully logged in to TavTenders'
      });
      navigate('/');
    } catch (error) {
      toast.error('Login failed', {
        description: error.message || 'Invalid credentials'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-background">
        <div className="login-gradient" />
        <div className="login-pattern" />
      </div>

      <div className="login-container">
        {/* Logo Section */}
        <div className="login-header">
          <div className="login-logo">
            <div className="login-logo-icon">
              <FileSearch size={32} />
            </div>
            <div>
              <h1 className="login-logo-text">TavTenders</h1>
              <p className="login-logo-subtitle">Tender Intelligence Platform</p>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="login-card" data-testid="login-card">
          <CardHeader className="login-card-header">
            <div className="login-shield-icon">
              <Shield size={24} />
            </div>
            <CardTitle className="login-card-title">Admin Login</CardTitle>
            <CardDescription className="login-card-description">
              Sign in to manage tenders, sites, and recipients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="login-field">
                <Label htmlFor="username" className="login-label">Email</Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="Enter your email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  className="login-input"
                  data-testid="login-username-input"
                  autoComplete="email"
                />
              </div>

              <div className="login-field">
                <Label htmlFor="password" className="login-label">Password</Label>
                <div className="login-password-wrapper">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="login-input login-password-input"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    data-testid="login-toggle-password"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="login-submit-button"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="login-footer">
          <p>Alephtav Consultancy & Mau Tech</p>
          <p className="login-footer-copyright">© 2025 TavTenders. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
